import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import 'moment-duration-format';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { DurationModel } from '../../../models/primitive/duration.model';

const inputMaxLength = 2;
const inputMinuteMaxValue = 59;
@Component({
    selector: 'pace-core-duration-manual-input',
    templateUrl: './duration-manual-input.component.html',
    styleUrls: ['./duration-manual-input.component.scss'],
})
export class DurationManualInputComponent implements OnInit, AfterViewInit {
    public durationManualInputForm: FormGroup;

    public hoursInputControl: FormControl;
    public minutesInputControl: FormControl;
    public inputMaxLength = inputMaxLength;
    public inputMinuteMaxValue = inputMinuteMaxValue;

    public get hours(): number {
        return this.hoursInputControl ? parseInt(this.hoursInputControl.value as string, 10) : NaN;
    }

    public get minutes(): number {
        return this.minutesInputControl ? parseInt(this.minutesInputControl.value as string, 10) : NaN;
    }

    @Input()
    public model: DurationModel;

    @Output()
    public modelChanged = new EventEmitter<DurationModel>();

    @ViewChild('hoursInput')
    public hourInputElement: ElementRef;

    @ViewChild('minutesInput')
    public minutesInputElement: ElementRef;
    public constructor(
        private readonly translate: TranslateService,
        public readonly modalInstance: BsModalRef,
        private readonly formBuilder: FormBuilder,
    ) {}

    public ngOnInit(): void {
        const hours =
            this.model.value && this.model.value.asMilliseconds() > 0
                ? moment.utc(this.model.value.asMilliseconds()).format('H')
                : '';
        const minutes =
            this.model.value && this.model.value.asMilliseconds() > 0
                ? moment.utc(this.model.value.asMilliseconds()).format('mm')
                : '';

        const durationValidator = (): ValidationErrors | null => this.checkDuration();

        // eslint-disable-next-line @typescript-eslint/unbound-method
        this.hoursInputControl = new FormControl(hours, [Validators.required, Validators.min(0)]);
        this.minutesInputControl = new FormControl(minutes, [
            // eslint-disable-next-line @typescript-eslint/unbound-method
            Validators.required,
            Validators.min(0),
            Validators.max(inputMinuteMaxValue),
        ]);

        this.durationManualInputForm = this.formBuilder.group(
            {
                hoursInputControl: this.hoursInputControl,
                minutesInputControl: this.minutesInputControl,
            },
            { validators: durationValidator },
        );
    }

    public ngAfterViewInit(): void {
        if (this.hourInputElement) {
            setTimeout(() => {
                (<HTMLInputElement>this.hourInputElement.nativeElement).focus();
            }, 0);
        }
    }

    private checkDuration(): ValidationErrors | null {
        const errors: ValidationErrors = {};

        const potentialDuration = moment.duration({
            hours: this.hours,
            minutes: this.minutes,
        });

        if (potentialDuration.asMilliseconds() > this.model.options.maxValue.asMilliseconds()) {
            // moment.duration is not being formatted correctly if the value of the duration is greater than 24 hours.
            // If the value is greater than 24 hours, then the format on the duration will return just the hours which are greater than 24 hours
            // E.g. If the user is entering 25 hours, the returned value by the format function of the duration will be 1.
            // The moment-duration-format plugin offer the format function which is working as expected
            // check following link for more details - https://stackoverflow.com/questions/45629183/formatting-momentjs-duration-above-24-hours
            errors.max = <string>this.translate.instant('DURATION_MANUAL_INPUT_MAX_ERROR', {
                duration: potentialDuration.format('HH:mm'),
                maxDuration: this.model.options.maxValue.format('HH:mm'),
            });
        }

        if (potentialDuration.asMilliseconds() < this.model.options.minValue.asMilliseconds()) {
            errors.min = <string>this.translate.instant('DURATION_MANUAL_INPUT_MIN_ERROR', {
                duration: potentialDuration.format('HH:mm'),
                minDuration: this.model.options.minValue.format('HH:mm'),
            });
        }

        return Object.keys(errors).length > 0 ? errors : null;
    }

    public onSave(): void {
        const duration = moment.duration({
            hours: this.hours,
            minutes: this.minutes,
        });
        this.model.value = duration;
        this.modelChanged.emit(new DurationModel(this.model.name, duration, this.model.options));
        this.modalInstance.hide();
    }

    public objectToArray(errors: { [key: string]: string } | null): string[] {
        return errors === null ? [] : Object.values(errors);
    }

    public submitDurationOnEnter(): void {
        if (this.durationManualInputForm.valid) {
            this.onSave();
        }
    }

    public onHoursKeyUp(e: KeyboardEvent, input: HTMLInputElement): void {
        if (input.value.length === inputMaxLength) {
            setTimeout(() => {
                (<HTMLInputElement>this.minutesInputElement.nativeElement).focus();
            }, 1);
        }
    }
}
